import api from './api';

/**
 * Permissions Service
 * Frontend API wrapper for the permissions system
 */

/**
 * Get all permission categories and their permissions
 * @returns {Promise<{success: boolean, totalPermissions: number, categories: Array}>}
 */
export const getPermissions = async () => {
    const response = await api.get('/permissions');
    return response.data;
};

/**
 * Get flat list of all permissions
 * @returns {Promise<{success: boolean, count: number, permissions: Array}>}
 */
export const getPermissionsList = async () => {
    const response = await api.get('/permissions/list');
    return response.data;
};

/**
 * Get permission category metadata
 * @returns {Promise<{success: boolean, categories: Array}>}
 */
export const getCategories = async () => {
    const response = await api.get('/permissions/categories');
    return response.data;
};

/**
 * Get default permission sets (basic employee, full admin)
 * @returns {Promise<{success: boolean, defaults: Object}>}
 */
export const getPermissionDefaults = async () => {
    const response = await api.get('/permissions/defaults');
    return response.data;
};

/**
 * Check if a user has a specific permission
 * @param {Object} userPermissions - User's permission object
 * @param {string} permissionKey - Permission key to check (e.g., 'task.create')
 * @param {string} requiredScope - Required scope level (optional, for scoped permissions)
 * @returns {boolean}
 */
export const hasPermission = (userPermissions, permissionKey, requiredScope = null) => {
    if (!userPermissions) return false;

    const value = userPermissions[permissionKey];

    // Permission not set = denied
    if (value === undefined || value === null || value === false || value === 'none') {
        return false;
    }

    // Simple permission (boolean)
    if (value === true) {
        return true;
    }

    // Scoped permission - check scope hierarchy (matches permissions.config.js)
    if (requiredScope) {
        const scopeHierarchy = ['none', 'own', 'team', 'all'];
        const userScopeIndex = scopeHierarchy.indexOf(value);
        const requiredScopeIndex = scopeHierarchy.indexOf(requiredScope);

        // User's scope must be >= required scope
        return userScopeIndex >= requiredScopeIndex;
    }

    // If no required scope specified, any non-none scope grants access
    return value !== 'none';
};

/**
 * Get effective scope for a scoped permission
 * @param {Object} userPermissions - User's permission object
 * @param {string} permissionKey - Permission key
 * @returns {string|null} - Scope value or null if no access
 */
export const getPermissionScope = (userPermissions, permissionKey) => {
    if (!userPermissions) return null;

    const value = userPermissions[permissionKey];

    if (value === undefined || value === null || value === false || value === 'none') {
        return null;
    }

    if (value === true) {
        return 'all'; // Boolean true = full access
    }

    return value;
};

/**
 * Filter an array of items based on user's scoped permission
 * @param {Array} items - Items to filter
 * @param {Object} userPermissions - User's permissions
 * @param {string} permissionKey - Permission to check
 * @param {Object} context - Context with userId, teamIds for scope checking
 * @returns {Array} - Filtered items
 */
export const filterByPermission = (items, userPermissions, permissionKey, context) => {
    const scope = getPermissionScope(userPermissions, permissionKey);

    if (!scope) return [];
    if (scope === 'all') return items;

    return items.filter(item => {
        if (scope === 'own_created') {
            return item.created_by === context.userId || item.creator_id === context.userId;
        }

        if (scope === 'own_team') {
            // Check if item's team matches any of user's teams
            const itemTeamId = item.team_id || item.teamId;
            return context.teamIds?.includes(itemTeamId);
        }

        return false;
    });
};

export default {
    getPermissions,
    getPermissionsList,
    getCategories,
    getPermissionDefaults,
    hasPermission,
    getPermissionScope,
    filterByPermission
};
